/**
 * Budget Assignment Section for Project Part Detail Page
 *
 * Allows assigning budget from investment lines to project parts.
 * Uses extracted components for better maintainability:
 * - BudgetAssignmentCard: Expandable card for viewing/editing assignments
 * - AddBudgetDialog: Dialog for adding new budget assignments
 */

import React, { useState, useEffect, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Plus, CurrencyEur } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProjectPart } from "shared/types/renovation";
import { UnitDTO } from "shared/types/unit";
import { BudgetAssignmentCard, AddBudgetDialog, formatCurrency } from "./budget-assignment";

interface PartBudgetAssignmentSectionProps {
  part: ProjectPart;
  buildingId: string;
  onUpdate: () => void;
}

export const PartBudgetAssignmentSection: React.FC<PartBudgetAssignmentSectionProps> = ({
  part,
  buildingId,
  onUpdate,
}) => {
  const { session } = useSession();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);

  // Fetch building units for the assignment cards
  useEffect(() => {
    if (!buildingId || !session) return;

    fetchWithAuth(
      `${API_URL}/units/building/${buildingId}`,
      { method: "GET" },
      session
    )
      .then((res) => res.json())
      .then((data) => {
        setBuildingUnits(Array.isArray(data) ? data : data.data || []);
      })
      .catch((err) => console.error("Error loading units:", err));
  }, [buildingId, session]);

  // Get investment assignments (new system) - this is the source of truth
  const investmentAssignments = (part as any).investmentAssignments || [];

  // Calculate total assigned budget from investment assignments
  const totalAssignedBudget = useMemo(() => {
    return investmentAssignments.reduce(
      (sum: number, assignment: any) => sum + Number(assignment.assignedAmount || 0),
      0
    );
  }, [investmentAssignments]);

  const hasAssignments = investmentAssignments.length > 0;

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-4 flex items-center justify-between border-b border-neutral-100 pb-2">
          <h4 className="text-sm font-medium text-neutral-700">Budget toewijzing</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
            className="border-moss-primary/30 text-moss-primary hover:bg-moss-primary/10"
          >
            <Plus className="mr-1 h-4 w-4" />
            Budget toewijzen
          </Button>
        </div>

        <div className="space-y-3">
          {hasAssignments ? (
            <>
              {investmentAssignments.map((assignment: any) => (
                <BudgetAssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  buildingUnits={buildingUnits}
                  onUpdate={onUpdate}
                />
              ))}

              {/* Total */}
              <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
                <span className="text-sm font-medium text-neutral-700">Totaal toegewezen:</span>
                <span className="text-lg font-bold text-moss-primary">
                  {formatCurrency(totalAssignedBudget)}
                </span>
              </div>
            </>
          ) : (
            <div className="rounded-md bg-neutral-50 py-6 text-center">
              <CurrencyEur className="mx-auto mb-2 h-8 w-8 text-neutral-300" />
              <p className="text-sm text-neutral-500">Nog geen budget toegewezen</p>
              <p className="mt-1 text-xs text-neutral-400">
                Klik op "Budget toewijzen" om budget van investeringslijnen te koppelen
              </p>
            </div>
          )}
        </div>

        {/* Add Budget Dialog */}
        <AddBudgetDialog
          part={part}
          buildingId={buildingId}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSuccess={onUpdate}
        />
      </div>
    </TooltipProvider>
  );
};

export default PartBudgetAssignmentSection;
